import React, { useState, useEffect } from 'react';
import { FiSave, FiRotateCcw, FiDollarSign, FiFileText, FiSettings } from 'react-icons/fi';
import adminSettingsService from '../../../services/adminSettingsService';
import { Loading } from '../../../components/UI/Loading';

const InvoicingSettings = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const result = await adminSettingsService.getInvoicingSettings();
      if (result.success) {
        setSettings(result.data);
        setOriginalSettings(result.data);
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al cargar configuraciones' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const result = await adminSettingsService.updateInvoicingSettings(settings);
      if (result.success) {
        setMessage({ type: 'success', text: 'Configuración actualizada correctamente' });
        setOriginalSettings(settings);
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al actualizar' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('¿Desea descartar los cambios realizados?')) {
      setSettings(originalSettings);
      setMessage(null);
    }
  };

  if (isLoading) {
    return <Loading message="Cargando configuraciones de facturación..." />;
  }

  if (!settings) {
    return (
      <div className="error-container">
        <p>No se pudieron cargar las configuraciones</p>
        <button onClick={loadSettings} className="btn btn--primary">Reintentar</button>
      </div>
    );
  }

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  return (
    <div className="settings-page">
      {message && (
        <div className={`alert alert--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Sección: Numeración de Facturas */}
      <div className="settings-section">
        <div className="section-header">
          <FiFileText size={24} />
          <div>
            <h3 className="section-title">Numeración de Facturas</h3>
            <p className="section-description">Configuración del formato y secuencia de numeración</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Prefijo de Facturas</label>
            <input
              type="text"
              className="form-input"
              value={settings.invoice_prefix || ''}
              onChange={(e) => handleChange('invoice_prefix', e.target.value)}
              placeholder="BN"
            />
            <small className="form-helper">Prefijo para números de factura (ej: BN-000001)</small>
          </div>

          <div className="form-group">
            <label className="form-label">Número Inicial</label>
            <input
              type="number"
              className="form-input"
              value={settings.invoice_starting_number || 1000}
              onChange={(e) => handleChange('invoice_starting_number', parseInt(e.target.value))}
              min="1"
            />
            <small className="form-helper">Primera factura comienza desde este número</small>
          </div>

          <div className="form-group">
            <label className="form-label">Dígitos de Relleno</label>
            <input
              type="number"
              className="form-input"
              value={settings.invoice_number_padding || 6}
              onChange={(e) => handleChange('invoice_number_padding', parseInt(e.target.value))}
              min="4"
              max="10"
            />
            <small className="form-helper">Cantidad de dígitos para el número (con ceros a la izquierda)</small>
          </div>
        </div>
      </div>

      {/* Sección: Impuestos y Moneda */}
      <div className="settings-section">
        <div className="section-header">
          <FiDollarSign size={24} />
          <div>
            <h3 className="section-title">Impuestos y Moneda</h3>
            <p className="section-description">Configuración de impuestos, moneda y precios</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Tasa de Impuesto por Defecto (%)</label>
            <input
              type="number"
              className="form-input"
              value={settings.default_tax_rate || 19}
              onChange={(e) => handleChange('default_tax_rate', parseFloat(e.target.value))}
              min="0"
              max="100"
              step="0.01"
            />
            <small className="form-helper">Porcentaje de impuesto aplicado a las facturas</small>
          </div>

          <div className="form-group">
            <label className="form-label">Símbolo de Moneda</label>
            <input
              type="text"
              className="form-input"
              value={settings.currency_symbol || '$'}
              onChange={(e) => handleChange('currency_symbol', e.target.value)}
              maxLength="5"
            />
            <small className="form-helper">Símbolo mostrado en facturas</small>
          </div>

          <div className="form-group">
            <label className="form-label">Código de Moneda</label>
            <input
              type="text"
              className="form-input"
              value={settings.currency_code || 'USD'}
              onChange={(e) => handleChange('currency_code', e.target.value.toUpperCase())}
              maxLength="3"
            />
            <small className="form-helper">Código ISO de moneda (ej: COP, USD)</small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.tax_included_in_prices || false}
                onChange={(e) => handleChange('tax_included_in_prices', e.target.checked)}
              />
              <span>Impuestos incluidos en precios</span>
            </label>
            <small className="form-helper">Los precios de productos ya incluyen impuestos</small>
          </div>
        </div>
      </div>

      {/* Sección: Términos de Pago */}
      <div className="settings-section">
        <div className="section-header">
          <FiSettings size={24} />
          <div>
            <h3 className="section-title">Términos de Pago</h3>
            <p className="section-description">Configuración de plazos y recargos</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Términos de Pago por Defecto (días)</label>
            <input
              type="number"
              className="form-input"
              value={settings.default_payment_terms || 30}
              onChange={(e) => handleChange('default_payment_terms', parseInt(e.target.value))}
              min="0"
            />
            <small className="form-helper">Días de plazo para pago de facturas</small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.late_fee_enabled || false}
                onChange={(e) => handleChange('late_fee_enabled', e.target.checked)}
              />
              <span>Habilitar recargo por mora</span>
            </label>
            <small className="form-helper">Aplicar recargo automático a facturas vencidas</small>
          </div>

          {settings.late_fee_enabled && (
            <div className="form-group">
              <label className="form-label">Tasa de Recargo por Mora (%)</label>
              <input
                type="number"
                className="form-input"
                value={settings.late_fee_rate || 0}
                onChange={(e) => handleChange('late_fee_rate', parseFloat(e.target.value))}
                min="0"
                max="100"
                step="0.01"
              />
              <small className="form-helper">Porcentaje de recargo mensual por mora</small>
            </div>
          )}

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.allow_partial_payments ?? true}
                onChange={(e) => handleChange('allow_partial_payments', e.target.checked)}
              />
              <span>Permitir pagos parciales</span>
            </label>
            <small className="form-helper">Permitir que las facturas se paguen en varias cuotas</small>
          </div>
        </div>
      </div>

      {/* Sección: Información de la Empresa */}
      <div className="settings-section">
        <div className="section-header">
          <FiFileText size={24} />
          <div>
            <h3 className="section-title">Información de la Empresa</h3>
            <p className="section-description">Datos que aparecen en las facturas</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group form-group--full">
            <label className="form-label">Nombre de la Empresa</label>
            <input
              type="text"
              className="form-input"
              value={settings.company_name || ''}
              onChange={(e) => handleChange('company_name', e.target.value)}
              placeholder="Betel Natural Inc"
            />
          </div>

          <div className="form-group form-group--full">
            <label className="form-label">Dirección</label>
            <input
              type="text"
              className="form-input"
              value={settings.company_address || ''}
              onChange={(e) => handleChange('company_address', e.target.value)}
              placeholder="108 S Calavo St - Unit B, Santa Paula, CA, 93060"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Teléfono</label>
            <input
              type="text"
              className="form-input"
              value={settings.company_phone || ''}
              onChange={(e) => handleChange('company_phone', e.target.value)}
              placeholder="805 229-7242"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={settings.company_email || ''}
              onChange={(e) => handleChange('company_email', e.target.value)}
              placeholder="info@betelnatural.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">NIT / Tax ID</label>
            <input
              type="text"
              className="form-input"
              value={settings.company_tax_id || ''}
              onChange={(e) => handleChange('company_tax_id', e.target.value)}
              placeholder="123456789-0"
            />
          </div>

          <div className="form-group form-group--full">
            <label className="form-label">Texto del Pie de Factura</label>
            <textarea
              className="form-input"
              value={settings.invoice_footer_text || ''}
              onChange={(e) => handleChange('invoice_footer_text', e.target.value)}
              rows="3"
              placeholder="Gracias por su preferencia..."
            />
          </div>
        </div>
      </div>

      {/* Botones de Acción */}
      <div className="settings-actions">
        <button
          className="btn btn--outline"
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
        >
          <FiRotateCcw size={16} />
          Descartar Cambios
        </button>

        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          <FiSave size={16} />
          {isSaving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
};

export default InvoicingSettings;
